import { Controller } from "@hotwired/stimulus"

// Handles click-to-create (month), click+drag-to-create (week), and the
// "New Event" header button. Manages a fixed-position popover with the form.
//
// Events only. Seasons used to share this popover behind a second tab, but a
// season is a three-step setup that does not fit a 340px sheet, so it has its
// own page and this is a single-purpose control again.
export default class extends Controller {
  static targets = ["popover", "formContainer", "formTemplate"]
  static values = {
    view: String,
    hourHeight: { type: Number, default: 48 },
  }

  connect() {
    this._dragging = false
    this._dragCol = null
    this._dragStartMinute = 0
    this._dragCurrentMinute = 0
    this._preview = null
    this._anchor = null

    this._onMouseMove = this._onMouseMove.bind(this)
    this._onMouseUp = this._onMouseUp.bind(this)
    this._onOutsideMousedown = this._onOutsideMousedown.bind(this)
    this._onKeydown = this._onKeydown.bind(this)
    this._onResize = this._onResize.bind(this)

    document.addEventListener("mousedown", this._onOutsideMousedown)
    document.addEventListener("keydown", this._onKeydown)
    window.addEventListener("resize", this._onResize)
  }

  disconnect() {
    document.removeEventListener("mousemove", this._onMouseMove)
    document.removeEventListener("mouseup", this._onMouseUp)
    document.removeEventListener("mousedown", this._onOutsideMousedown)
    document.removeEventListener("keydown", this._onKeydown)
    window.removeEventListener("resize", this._onResize)
    this._removePreview()
  }

  // ── Actions ──────────────────────────────────────────────────────────

  // Header "New Event" button
  openFromButton(event) {
    event.stopPropagation()
    this._openPopover(event.currentTarget, null, null)
  }

  // Month view: click empty space in a day cell
  clickDay(event) {
    if (event.target.closest("[data-controller='calendar']")) return
    event.stopPropagation()

    const date = event.currentTarget.dataset.calendarCreateDateParam
    if (!date) return

    this._openPopover(event.currentTarget, `${date}T18:00`, `${date}T19:00`)
  }

  // Week view: mousedown on a day column starts drag
  startDrag(event) {
    if (event.target.closest("[data-controller='calendar']")) return
    if (event.button !== 0) return

    this._hidePopover()

    const col = event.currentTarget
    this._dragCol = col
    this._dragDate = col.dataset.calendarCreateDateParam
    if (!this._dragDate) return

    const rect = col.getBoundingClientRect()
    const y = event.clientY - rect.top
    const minute = this._yToMinute(y)

    this._dragStartMinute = this._snap(minute)
    this._dragCurrentMinute = this._dragStartMinute + 60
    this._dragging = true

    this._createPreview(col)
    this._updatePreview()

    document.addEventListener("mousemove", this._onMouseMove)
    document.addEventListener("mouseup", this._onMouseUp)

    event.preventDefault()
  }

  // Called by the form's Cancel / X buttons
  dismiss(event) {
    event?.stopPropagation()
    this._hidePopover()
  }

  // ── Drag handling (week view) ────────────────────────────────────────

  _onMouseMove(event) {
    if (!this._dragging || !this._dragCol) return

    const rect = this._dragCol.getBoundingClientRect()
    const y = event.clientY - rect.top
    const minute = this._snap(this._yToMinute(y))

    if (minute !== this._dragStartMinute) {
      this._dragCurrentMinute = minute
    } else {
      this._dragCurrentMinute = this._dragStartMinute + 60
    }

    this._updatePreview()
  }

  _onMouseUp() {
    document.removeEventListener("mousemove", this._onMouseMove)
    document.removeEventListener("mouseup", this._onMouseUp)

    if (!this._dragging) return
    this._dragging = false

    const startMin = Math.min(this._dragStartMinute, this._dragCurrentMinute)
    const endMin = Math.max(this._dragStartMinute, this._dragCurrentMinute)
    const finalEnd = endMin <= startMin ? startMin + 60 : endMin

    const startTime = this._minuteToTimeStr(startMin)
    const endTime = this._minuteToTimeStr(Math.min(finalEnd, 24 * 60))

    // Use the preview element (or the column) as the anchor for popover placement
    const anchor = this._preview || this._dragCol
    this._openPopover(
      anchor,
      `${this._dragDate}T${startTime}`,
      `${this._dragDate}T${endTime}`,
    )
    this._removePreview()
  }

  // ── Drag preview ────────────────────────────────────────────────────

  _createPreview(col) {
    this._removePreview()
    const el = document.createElement("div")
    el.className =
      "absolute left-0.5 right-0.5 rounded bg-vivid-purple/20 border border-vivid-purple/40 pointer-events-none z-10"
    col.appendChild(el)
    this._preview = el
  }

  _updatePreview() {
    if (!this._preview) return
    const hourHeight = this.hourHeightValue
    const startMin = Math.min(this._dragStartMinute, this._dragCurrentMinute)
    const endMin = Math.max(this._dragStartMinute, this._dragCurrentMinute)
    const top = (startMin / 60) * hourHeight
    const height = Math.max(
      ((endMin - startMin) / 60) * hourHeight,
      (15 / 60) * hourHeight,
    )
    this._preview.style.top = `${top}px`
    this._preview.style.height = `${height}px`
  }

  _removePreview() {
    if (this._preview) {
      this._preview.remove()
      this._preview = null
    }
  }

  // ── Popover management ──────────────────────────────────────────────

  _openPopover(anchor, startsAt, endsAt) {
    // Reset form from the clean template
    if (this.hasFormTemplateTarget && this.hasFormContainerTarget) {
      this.formContainerTarget.innerHTML = this.formTemplateTarget.innerHTML
    }

    // Default to Event mode every time the popover opens
    // Pre-fill dates on the event form
    if (startsAt) {
      const eventForm = this.popoverTarget.querySelector("form")
      if (eventForm) {
        const startInput = eventForm.querySelector(
          "[name='group_event[starts_at]']",
        )
        const endInput = eventForm.querySelector(
          "[name='group_event[ends_at]']",
        )
        if (startInput) startInput.value = startsAt
        if (endInput) endInput.value = endsAt
      }
    }

    // Show & position
    this._anchor = anchor
    this.popoverTarget.classList.remove("hidden")
    this._positionPopover(anchor, this.popoverTarget)
  }

  _hidePopover() {
    if (!this.hasPopoverTarget) return
    this.popoverTarget.classList.add("hidden")
    this._anchor = null
  }

  _onResize() {
    if (!this.hasPopoverTarget) return
    if (this.popoverTarget.classList.contains("hidden")) return
    if (!this._anchor || !this._anchor.isConnected) return
    this._positionPopover(this._anchor, this.popoverTarget)
  }

  _onKeydown(event) {
    if (event.key !== "Escape") return
    if (!this.hasPopoverTarget) return
    if (this.popoverTarget.classList.contains("hidden")) return
    event.stopPropagation()
    this._hidePopover()
  }

  _onOutsideMousedown(event) {
    if (!this.hasPopoverTarget) return
    if (this.popoverTarget.classList.contains("hidden")) return
    if (this.popoverTarget.contains(event.target)) return
    this._hidePopover()
  }

  // Position the popover using fixed coordinates, clamped to the viewport.
  // Caps the popover's max-height so an over-tall form scrolls inside the
  // popover instead of overflowing off-screen. Re-anchors against the
  // clicked day cell on every call (including window resize).
  _positionPopover(anchor, popover) {
    const padding = 8

    // Reset constraints to measure natural width/height
    popover.style.visibility = "hidden"
    popover.style.left = "0px"
    popover.style.top = "0px"
    popover.style.maxHeight = ""

    const vw = window.innerWidth
    const vh = window.innerHeight
    const maxHeight = vh - padding * 2
    popover.style.maxHeight = `${maxHeight}px`

    const anchorRect = anchor.getBoundingClientRect()
    const pw = popover.offsetWidth
    const ph = popover.offsetHeight

    // Preferred: to the right of the anchor, top-aligned
    let left = anchorRect.right + 8
    let top = anchorRect.top
    let placement = "right"

    // If no room to the right, try left of the anchor
    if (left + pw + padding > vw) {
      const leftOfAnchor = anchorRect.left - pw - 8
      if (leftOfAnchor >= padding) {
        left = leftOfAnchor
        placement = "left"
      } else {
        // Neither side fits — drop below or above and align horizontally.
        const belowTop = anchorRect.bottom + 8
        if (belowTop + ph + padding <= vh) {
          left = anchorRect.left
          top = belowTop
          placement = "below"
        } else {
          left = anchorRect.left
          top = anchorRect.top - ph - 8
          placement = "above"
        }
      }
    }

    // Final viewport clamp on both axes
    if (left + pw + padding > vw) left = vw - pw - padding
    if (left < padding) left = padding
    if (top + ph + padding > vh) top = vh - ph - padding
    if (top < padding) top = padding

    popover.style.left = `${left}px`
    popover.style.top = `${top}px`
    popover.dataset.placement = placement
    popover.style.visibility = ""
  }

  // ── Helpers ─────────────────────────────────────────────────────────

  _yToMinute(y) {
    const hourHeight = this.hourHeightValue
    return Math.max(0, Math.min(24 * 60 - 1, (y / hourHeight) * 60))
  }

  _snap(minute) {
    return Math.round(minute / 15) * 15
  }

  _minuteToTimeStr(totalMinutes) {
    const h = Math.floor(totalMinutes / 60)
    const m = totalMinutes % 60
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`
  }
}
